export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  userType?: string;
  isAdmin?: boolean;
}

export interface ImpersonationData {
  originalAdminToken: string;
  originalAdminUser: User;
  impersonatedUser: User;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

class AuthService {
  private static instance: AuthService;
  private token: string | null = null;
  private user: User | null = null;
  private onLogout: (() => void) | null = null;

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  constructor() {
    // Check for impersonation mode first
    const impersonationData = this.getImpersonationData();

    if (impersonationData) {
      // In impersonation mode - use impersonated user's data but admin's token
      this.token = impersonationData.originalAdminToken;
      this.user = impersonationData.impersonatedUser;
      console.log('🎭 Loaded impersonation mode:', this.user.email);
    } else {
      // Normal mode - use stored token and user
      this.token = localStorage.getItem('chime_token');

      const savedUser = localStorage.getItem('chime_user');
      if (savedUser) {
        this.user = JSON.parse(savedUser);
      }
    }

    // Check if stored token has expired
    this.checkTokenExpiry();
  }

  setLogoutCallback(callback: () => void) {
    this.onLogout = callback;
  }

  private checkTokenExpiry(): void {
    const expiresAt = localStorage.getItem('chime_token_expires_at');
    if (expiresAt && this.token) {
      const expiryTime = parseInt(expiresAt, 10);
      const now = Date.now();
      
      if (now >= expiryTime) {
        console.log('🔐 Token expired, clearing authentication');
        this.logout();
      } else {
        const remainingTime = Math.round((expiryTime - now) / (1000 * 60)); // minutes
        console.log(`🔐 Token valid for ${remainingTime} more minutes`);
      }
    }
  }

  getToken(): string | null {
    return this.token;
  }

  getUser(): User | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  setAuth(token: string, user: User): void {
    this.token = token;
    this.user = user;
    localStorage.setItem('chime_token', token);
    localStorage.setItem('chime_user', JSON.stringify(user));
  }

  isAdmin(): boolean {
    if (!this.user) return false;

    // Admin status comes from the explicit isAdmin flag on the user record.
    // Staff (@exac.com / @advita.com) employees are NOT admins by default;
    // an admin must be explicitly promoted on the backend.
    const impersonationData = this.getImpersonationData();
    if (impersonationData) {
      return impersonationData.originalAdminUser.isAdmin === true;
    }

    return this.user.isAdmin === true;
  }

  startImpersonation(impersonatedUser: User): void {
    if (!this.token || !this.user) {
      throw new Error('Must be authenticated to start impersonation');
    }

    const impersonationData: ImpersonationData = {
      originalAdminToken: this.token,
      originalAdminUser: this.user,
      impersonatedUser: impersonatedUser
    };

    localStorage.setItem('chime_impersonation', JSON.stringify(impersonationData));

    // Update current state to impersonated user
    this.user = impersonatedUser;
    console.log('🎭 Started impersonation:', impersonatedUser.email);
  }

  stopImpersonation(): void {
    const impersonationData = this.getImpersonationData();
    if (!impersonationData) {
      return;
    }

    // Restore original admin
    this.token = impersonationData.originalAdminToken;
    this.user = impersonationData.originalAdminUser;

    localStorage.removeItem('chime_impersonation');
    console.log('🎭 Stopped impersonation, restored admin:', this.user.email);
  }

  getImpersonationData(): ImpersonationData | null {
    const data = localStorage.getItem('chime_impersonation');
    if (!data) return null;

    try {
      return JSON.parse(data) as ImpersonationData;
    } catch {
      return null;
    }
  }

  isImpersonating(): boolean {
    return this.getImpersonationData() !== null;
  }

  handleAuthError(): void {
    console.warn('🚨 Handling authentication error - clearing auth state');

    // Clear auth state without triggering reload
    this.token = null;
    this.user = null;

    // Clear localStorage
    localStorage.removeItem('chime_token');
    localStorage.removeItem('chime_user');
    localStorage.removeItem('chime_token_expires_at');
    localStorage.removeItem('chime_refresh_token');
    localStorage.removeItem('chime_impersonation');

    // Notify the store about logout (this will trigger re-render)
    if (this.onLogout) {
      this.onLogout();
    }

    // Don't reload the page - let React handle the re-render to login screen
  }

  logout(): void {
    const currentUser = this.user;
    this.token = null;
    this.user = null;

    // Preserve theme before clearing
    const currentTheme = localStorage.getItem('chime_theme');
    console.log('🎨 Preserving theme during logout:', currentTheme);

    // Clear all authentication-related localStorage items
    localStorage.removeItem('chime_token');
    localStorage.removeItem('chime_user');
    localStorage.removeItem('chime_token_expires_at');
    localStorage.removeItem('chime_refresh_token');
    localStorage.removeItem('chime_impersonation');

    // Clear notification history and drafts that might contain user-specific data
    localStorage.removeItem('chime_notification_history');
    localStorage.removeItem('chime_drafts');
    localStorage.removeItem('chime_current_draft');
    localStorage.removeItem('chime_notification_settings');

    // Clear any other session data except theme
    const keysToRemove: string[] = [];
    const keysToPreserve = ['chime_theme']; // Preserve theme preference
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('chime_') && !keysToPreserve.includes(key)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Restore theme if it was cleared
    if (currentTheme) {
      localStorage.setItem('chime_theme', currentTheme);
      console.log('✅ Theme restored after cleanup:', localStorage.getItem('chime_theme'));
    }

    // Clear session storage as well
    sessionStorage.clear();

    // Notify the store about logout
    if (this.onLogout) {
      this.onLogout();
    }

    // Check if user was authenticated via Azure AD (staff users with @exac.com or @advita.com)
    const isAzureADUser = currentUser &&
      currentUser.userType === 'staff' &&
      (currentUser.email.toLowerCase().endsWith('@exac.com') ||
       currentUser.email.toLowerCase().endsWith('@advita.com'));

    if (isAzureADUser) {
      // Redirect to Azure AD logout to clear SSO session
      console.log('🔐 Clearing Azure AD SSO session for:', currentUser?.email);

      // Azure AD logout endpoint with post_logout_redirect_uri
      const azureLogoutUrl = 'https://login.microsoftonline.com/9adaab1d-bd58-40a7-9255-69ab5f6c3189/oauth2/v2.0/logout' +
        '?post_logout_redirect_uri=' + encodeURIComponent(window.location.origin);

      window.location.href = azureLogoutUrl;
    } else {
      // For non-Azure AD users, just redirect to home
      setTimeout(() => {
        window.location.href = '/';
      }, 50);
    }
  }

  getAuthHeaders(includeContentType: boolean = true): Record<string, string> {
    const headers: Record<string, string> = {
      'X-Request-ID': this.generateRequestId(),
    };

    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    // Add impersonation header if in impersonation mode
    const impersonationData = this.getImpersonationData();
    if (impersonationData) {
      headers['X-Impersonate-User-ID'] = impersonationData.impersonatedUser.id;
      console.log('🎭 Adding impersonation header for user:', impersonationData.impersonatedUser.id);
    }

    return headers;
  }

  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }
}

export const authService = AuthService.getInstance();